import React, { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "redux/state";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import InfoOutlined from "@mui/icons-material/InfoOutlined";

import { openNavDrawer } from "components/NavDrawer/utils";

/**
 * The day the old org bar is removed. Everything in it already lives in the navigation drawer.
 * From this date (ISO, read as UTC) OrganizationSecondaryNav renders nothing, bar or notice.
 */
export const ORG_BAR_REMOVAL_DATE = "2026-11-30";

/** localStorage key prefixes; the user id is appended so the choice is per user. */
export const ORG_BAR_HIDDEN_KEY = "studio.orgBar.hidden";

export function formatOrgBarRemovalDate(isoDate: string): string {
    return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
}

function storageKey(prefix: string, userId: string | undefined): string {
    return `${prefix}:${userId || "anonymous"}`;
}

function readFlag(prefix: string, userId: string | undefined): boolean {
    try {
        return !!localStorage.getItem(storageKey(prefix, userId));
    } catch {
        return false;
    }
}

function writeFlag(prefix: string, userId: string | undefined): void {
    try {
        localStorage.setItem(storageKey(prefix, userId), new Date().toISOString());
    } catch {
        // Storage can be unavailable (private mode, blocked site data); the choice just won't stick.
    }
}

export function isOrgBarHidden(userId: string | undefined): boolean {
    return readFlag(ORG_BAR_HIDDEN_KEY, userId);
}

export function hideOrgBar(userId: string | undefined): void {
    writeFlag(ORG_BAR_HIDDEN_KEY, userId);
}

/**
 * The old top bars (org and global) during the move to the drawer: whether to show one, and how
 * to hide it. Hiding either bar hides both, and both go on the removal date.
 */
export function useMovingMenuBar(): { show: boolean; hide(): void } {
    const userId = useSelector((state: State) => state.auth.profile?.userId);
    const [hidden, setHidden] = useState<boolean>(() => isOrgBarHidden(userId));

    const hide = useCallback(() => {
        hideOrgBar(userId);
        setHidden(true);
    }, [userId]);

    return { show: !hidden && Date.now() < Date.parse(ORG_BAR_REMOVAL_DATE), hide };
}

export interface OrgBarTransitionNoticeProps {
    onHide(): void;
}

/**
 * Sits in the old org bar's right-justified slot, so it is clear which bar is going away.
 * "Hide this bar now" stays disabled until they have used "Show me" and seen where the items went.
 */
export const OrgBarTransitionNotice: FC<OrgBarTransitionNoticeProps> = (props) => {
    const { onHide } = props;

    const [shown, setShown] = useState<boolean>(false);

    function handleShowMe() {
        setShown(true);
        openNavDrawer();
    }

    return (
        <Box
            data-org-bar-notice
            role="status"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                my: 0.5,
                mr: 1,
                pl: 1.5,
                pr: 0.5,
                py: 0.25,
                whiteSpace: "nowrap",
                borderRadius: 999,
                bgcolor: "#fff4d6",
                border: "1px solid #f0c65a",
                color: "#5c3d00",
            }}
        >
            <InfoOutlined fontSize="small" sx={{ color: "#b7791f" }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
                We are moving this menu to the drawer menu soon.
            </Typography>
            <Button size="small" variant="contained" disableElevation onClick={handleShowMe} sx={{ borderRadius: 999 }}>
                Show me
            </Button>
            <Button size="small" color="inherit" onClick={onHide} disabled={!shown} sx={{ borderRadius: 999 }}>
                Hide this bar now
            </Button>
        </Box>
    );
};
